// pages/SalesPage.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  DollarSign,
  TrendingUp,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  FileSpreadsheet,
  FileText,
  X,
  RefreshCw,
  Wallet,
} from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Sale, SaleStats, SaleStatus } from "../../../../types/features/administration/BuyAndSell";
import useToken from "../../../../hooks/auth/useToken";
import useUsers from "../../../../hooks/administration/users/useUsers";
import { salesService } from "../../../../services/administration/buyAndSellService";
import SalesList from "../../../../components/features/administration/buy-and-sell/SalesList";
import SaleDetailsModal from "../../../../components/features/administration/buy-and-sell/SaleDetailsModal";


interface FilterState {
  search: string;
  status: string;
  currency: string;
  dateRange: string;
  startDate: string;
  endDate: string;
}

const SalesPage: React.FC = () => {
  const { token } = useToken();
  const { users } = useUsers({});

  // State
  const [activeTab, setActiveTab] = useState<string>("all");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [allSales, setAllSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    status: "",
    currency: "",
    dateRange: "",
    startDate: "",
    endDate: "",
  });

  const ITEMS_PER_PAGE = 20;

  const vendeursMap = useMemo(() => {
    const map: Record<string, any> = {};
    users.forEach((u) => {
      map[u.id!] = u;
    });
    return map;
  }, [users]);

  // Fetch all sales
  const fetchAllSales = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const response = await salesService.getSales(token, 100, 0);
      setAllSales(response.sales);
    } catch (error) {
      console.error("Error fetching sales:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter sales based on active tab and filters
  const filteredSales = useMemo(() => {
    let filtered = [...allSales];

    // Filter by tab
    if (activeTab === "today") {
      const today = new Date().toISOString().split("T")[0];
      filtered = filtered.filter((sale) => sale.created_at.startsWith(today));
    } else if (activeTab === "pending") {
      filtered = filtered.filter((sale) => sale.status === SaleStatus.PENDING);
    } else if (activeTab === "completed") {
      filtered = filtered.filter((sale) => sale.status === SaleStatus.COMPLETED);
    } else if (activeTab === "cancelled") {
      filtered = filtered.filter((sale) => sale.status === SaleStatus.CANCELLED);
    } else if (activeTab === "by_date" && selectedDate) {
      filtered = filtered.filter((sale) => sale.created_at.startsWith(selectedDate));
    }

    // Filter by search (ID, vendeur)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((sale) => {
        const vendeur = vendeursMap[sale.id_user_vendeur];
        const vendeurName = vendeur ? `${vendeur.first_name} ${vendeur.last_name}`.toLowerCase() : "";
        return (
          sale.id.toLowerCase().includes(searchLower) ||
          sale.id_user_vendeur.toLowerCase().includes(searchLower) ||
          vendeurName.includes(searchLower) ||
          (sale.id_user_acheteur && sale.id_user_acheteur.toLowerCase().includes(searchLower))
        );
      });
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter((sale) => sale.status === filters.status);
    }

    // Filter by currency
    if (filters.currency) {
      filtered = filtered.filter(
        (sale) => sale.monaie_vente === filters.currency || sale.monaie_achat === filters.currency
      );
    }

    // Filter by date range
    if (filters.startDate) {
      filtered = filtered.filter((sale) => sale.created_at.split("T")[0] >= filters.startDate);
    }
    if (filters.endDate) {
      filtered = filtered.filter((sale) => sale.created_at.split("T")[0] <= filters.endDate);
    }

    // Filter by date range preset
    if (filters.dateRange) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const todayStr = today.toISOString().split("T")[0];

      switch (filters.dateRange) {
        case "today": {
          filtered = filtered.filter((sale) => sale.created_at.startsWith(todayStr));
          break;
        }
        case "yesterday": {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          filtered = filtered.filter((sale) => sale.created_at.startsWith(yesterday.toISOString().split("T")[0]));
          break;
        }
        case "last7days": {
          const last7Days = new Date(today);
          last7Days.setDate(last7Days.getDate() - 7);
          filtered = filtered.filter((sale) => sale.created_at.split("T")[0] >= last7Days.toISOString().split("T")[0]);
          break;
        }
        case "last30days": {
          const last30Days = new Date(today);
          last30Days.setDate(last30Days.getDate() - 30);
          filtered = filtered.filter((sale) => sale.created_at.split("T")[0] >= last30Days.toISOString().split("T")[0]);
          break;
        }
        default:
          break;
      }
    }

    return filtered;
  }, [allSales, activeTab, selectedDate, filters, vendeursMap]);

  // Calculate stats from filtered sales
  const stats = useMemo(() => {
    const statsData: SaleStats = {
      total_amount: 0,
      total_sales: filteredSales.length,
      completed_count: 0,
      completed_amount: 0,
      pending_count: 0,
      pending_amount: 0,
      cancelled_count: 0,
      cancelled_amount: 0,
      failed_count: 0,
      failed_amount: 0,
      unique_sellers: 0,
      unique_buyers: 0,
    };

    const uniqueSellers = new Set<string>();
    const uniqueBuyers = new Set<string>();

    for (const sale of filteredSales) {
      statsData.total_amount += sale.somme_a_recevoir;

      switch (sale.status) {
        case SaleStatus.COMPLETED:
          statsData.completed_count++;
          statsData.completed_amount += sale.somme_a_recevoir;
          break;
        case SaleStatus.PENDING:
          statsData.pending_count++;
          statsData.pending_amount += sale.somme_a_recevoir;
          break;
        case SaleStatus.CANCELLED:
          statsData.cancelled_count++;
          statsData.cancelled_amount += sale.somme_a_recevoir;
          break;

        default:
          break;
      }

      if (sale.id_user_vendeur) uniqueSellers.add(sale.id_user_vendeur);
      if (sale.id_user_acheteur) uniqueBuyers.add(sale.id_user_acheteur);
    }

    statsData.unique_sellers = uniqueSellers.size;
    statsData.unique_buyers = uniqueBuyers.size;

    return statsData;
  }, [filteredSales]);

  // Pagination
  const totalPages = Math.ceil(filteredSales.length / ITEMS_PER_PAGE);
  const paginatedSales = filteredSales.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + paginatedSales.length, filteredSales.length);

  // Fetch sales on mount and when token changes
  useEffect(() => {
    fetchAllSales();
  }, [token]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedDate, filters]);

  // Handle sale actions
  const handleSaleAction = async (action: string, sale: Sale, data?: any) => {
    switch (action) {
      case "view":
        setSelectedSale(sale);
        break;
      case "change-status":
        await salesService.changeSaleStatus(token, sale.id, data);
        break;
      default:
        break;
    }
  };

  const handleFiltersChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleExportAll = () => {
    if (filteredSales.length === 0) {
      alert("Aucune vente à exporter");
      return;
    }
    setShowExportModal(true);
  };

  const exportToExcel = (data: any[]) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ventes");

    const fileName = `ventes_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const exportToPDF = (data: any[]) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    doc.setFontSize(18);
    doc.text("Rapport des Ventes", 14, 15);
    doc.setFontSize(11);
    doc.text(`Généré le: ${new Date().toLocaleString("fr-FR")}`, 14, 22);
    doc.text(`Total ventes: ${filteredSales.length}`, 14, 28);

    const headers = Object.keys(data[0] || {});
    const rows = data.map((row) => headers.map((header) => row[header]));

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 35,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`ventes_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const handleExport = async (format: "excel" | "pdf") => {
    setShowExportModal(false);

    const exportData = filteredSales.map((sale, index) => ({
      "#": index + 1,
      "ID Vente": sale.id,
      Date: new Date(sale.created_at).toLocaleString("fr-FR"),
      "Vendeur ID": sale.id_user_vendeur,
      "Acheteur ID": sale.id_user_acheteur || "N/A",
      "Somme à Vendre": `${sale.somme_a_vendre} ${sale.monaie_vente}`,
      "Taux Vendeur": sale.taux_vendeur,
      "Somme à Recevoir": `${sale.somme_a_recevoir} ${sale.monaie_achat}`,
      "Taux Marchand": sale.taux_marchand,
      "Marge Marchand": `${sale.somme_marchand} ${sale.monaie_achat}`,
      Pays: sale.id_pays_vendeur,
      Statut: sale.status,
    }));

    if (format === "excel") {
      exportToExcel(exportData);
    } else {
      exportToPDF(exportData);
    }
  };

  const getTodayLabel = () => {
    const today = new Date();
    const options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "long", year: "numeric" };
    return today.toLocaleDateString("fr-FR", options);
  };

  const tabs = [
    { id: "all", label: "Toutes", icon: Wallet, color: "green" },
    { id: "today", label: getTodayLabel(), icon: Calendar, color: "purple" },
    { id: "pending", label: "En Attente", icon: Clock, color: "yellow" },
    { id: "completed", label: "Complétées", icon: CheckCircle, color: "green" },
    { id: "failed", label: "Échouées", icon: XCircle, color: "red" },
    { id: "cancelled", label: "Annulées", icon: AlertCircle, color: "orange" },
  ];

  // Calculate total margin
  const totalMargin = filteredSales.reduce((sum, sale) => sum + sale.somme_marchand, 0);

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-800">Ventes</h1>
        <p className="text-gray-600">Suivi et gestion des ventes de devises</p>
      </div>

      {/* Tabs Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex overflow-x-auto flex-1 space-x-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const getColorClasses = () => {
                  if (!isActive) return "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300";
                  switch (tab.color) {
                    case "purple":
                      return "border-purple-600 text-purple-600";
                    case "green":
                      return "border-green-600 text-green-600";
                    case "yellow":
                      return "border-yellow-600 text-yellow-600";
                    case "red":
                      return "border-red-600 text-red-600";
                    case "orange":
                      return "border-orange-600 text-orange-600";
                    default:
                      return "border-gray-600 text-gray-600";
                  }
                };
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-4 py-3 space-x-2 whitespace-nowrap border-b-2 transition-colors ${getColorClasses()}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={fetchAllSales}
              disabled={loading}
              className="flex gap-2 items-center p-3 ml-3 font-semibold text-white bg-gradient-to-r from-green-600 to-green-700 rounded-lg shadow-md transition-all hover:from-green-700 hover:to-green-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Rafraîchir</span>
            </button>
          </div>
        </div>

        {/* Date Search */}
        <div className="flex items-center mt-4 space-x-3">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={() => {
                if (selectedDate) setActiveTab("by_date");
              }}
              disabled={!selectedDate}
              className="px-4 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:bg-gray-300"
            >
              Rechercher par date
            </button>
            {activeTab === "by_date" && (
              <button
                onClick={() => {
                  setActiveTab("all");
                  setSelectedDate("");
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-4">
        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Volume Total Vendu</p>
              <p className="text-2xl font-bold text-green-600">{stats.total_amount.toLocaleString()} CAD</p>
              <p className="text-xs text-gray-500">{stats.total_sales} transactions</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Ventes Complétées</p>
              <p className="text-2xl font-bold text-blue-600">{stats.completed_count}</p>
              <p className="text-lg font-semibold text-blue-500">{stats.completed_amount.toLocaleString()} CAD</p>
            </div>
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">En Attente</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending_count}</p>
              <p className="text-lg font-semibold text-yellow-500">{stats.pending_amount.toLocaleString()} CAD</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Marge Totale</p>
              <p className="text-2xl font-bold text-purple-600">{totalMargin.toLocaleString()} CAD</p>
              <p className="text-xs text-gray-500">Vendeurs: {stats.unique_sellers}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Liste des Ventes */}
      <SalesList
        sales={paginatedSales}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onSaleAction={handleSaleAction}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        startIndex={startIndex}
        endIndex={endIndex}
        totalSales={filteredSales.length}
        loading={loading}
        vendeursMap={vendeursMap}
      />

      {/* Modal Détails */}
      {selectedSale && (
        <SaleDetailsModal
          sale={selectedSale}
          isOpen={!!selectedSale}
          onClose={() => setSelectedSale(null)}
          onSaleUpdated={fetchAllSales}
        />
      )}

    
    </>
  );
};

export default SalesPage;
